import { SimpleIconButton } from '@dreamstack/feature-components/src/layout/SimpleIconButton'
import { SimpleMuiButton } from '@dreamstack/feature-components/src/layout/SimpleMuiButton'
import { useFormatNumber } from '@dreamstack/i18n'
import { ArrowLeft } from '@dreamstack/icons/src/icon-components/ArrowLeft'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { cn } from '@dreamstack/util/src/cn'
import qs from 'qs'
import React, { useState } from 'react'
import { BTLCalcField } from './BuyToLetCalculator/BTLCalcField'
import { BTLCalcFieldWithSlider } from './BuyToLetCalculator/BTLCalcFieldWithSlider'
import { formatOptionsDecimal0 } from './BuyToLetCalculator/BTLCalcUtil'

const Container: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div
    className={cn(
      'col-span-full bg-accentroAquaVeryLight-full text-[20px] px-px-24 py-px-64',
      'md:py-px-80 md:px-px-48',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)
const HeaderTitle: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'font-semibold text-[24px] md:text-[28px] lg:text-[40px]',
      className
    )}
    {...rest}
  />
)
const PageNumberBlock: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'font-semibold text-[20px] md:text-[24px] lg:text-[28px]',
      className
    )}
    {...rest}
  />
)
const PageNumberDescription: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn('text-[20px] md:text-[24px] lg:text-[28px]', className)}
    {...rest}
  />
)
const HorizontalLine: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'border-t w-full my-px-48 border-[rgba(43, 45, 54, 0.2)]',
      className
    )}
    {...rest}
  />
)
const Description: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { show?: boolean }
> = ({ show, className, ...rest }) => (
  <div
    className={cn(
      'text-[16px] lg:text-[20px] mt-px-16 lg:mt-px-24 mb-px-32 md:mb-px-48 lg:mb-px-80',
      show ? 'visible' : 'invisible',
      className
    )}
    {...rest}
  />
)
const ContentContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => <div className={cn('my-px-24', className)} {...rest} />

export const BuyToLetCalculator: React.FC = () => {
  const [pageInd, setPageInd] = useState(0)

  const [priceValue, setPriceValue] = useState(481000)
  const [rentValue, setRentValue] = useState(1281)
  const [putDownValue, setPutDownValue] = useState(86480)
  const [interestRate, setInterestRate] = useState(1.02)
  const [repaymentRate, setRepaymentRate] = useState(2)
  const [propertyTax, setPropertyTax] = useState(6)
  const [notaryFees, setNotaryFees] = useState(2)
  const [agentFees, setAgentFees] = useState(0)
  const [appreciateValue, setAppreciateValue] = useState(3)
  const [rentalIncrease, setRentalIncrease] = useState(3)
  const [maintenanceValue, setMaintenanceValue] = useState(241)

  const format = useFormatNumber(formatOptionsDecimal0)

  const movePage = (direction: number) => {
    if (pageInd === 2 && direction > 0) {
      const urlQueryParams = qs.stringify(
        {
          priceValue,
          rentValue,
          notaryFees,
          putDownValue,
          agentFees,
          propertyTax,
          interestRate,
          appreciateValue,
          rentalIncrease,
          repaymentRate,
          maintenanceValue,
        },
        { addQueryPrefix: true }
      )

      window.open(`btl-results${urlQueryParams}`, '_blank')
    } else {
      setPageInd((val) => {
        return val + direction
      })
    }
  }

  const header = (
    <div>
      <HeaderTitle>Buy-To-Let Calculator</HeaderTitle>
      <Description show={pageInd === 0}>
        Already have a property in mind, and want to know if the numbers stack
        up? Simply follow the easy steps below using your real investment data,
        and you will be rewarded with a comprehensive cashflow and investment
        report. You’re welcome!
      </Description>
      {pageInd !== 3 && (
        <PageNumberBlock>Step {pageInd + 1} of 3</PageNumberBlock>
      )}
      {pageInd == 0 && (
        <PageNumberDescription>
          Purchase and Rent Information
        </PageNumberDescription>
      )}
      {pageInd == 1 && (
        <PageNumberDescription>
          Load Information, Taxes and additional Costs
        </PageNumberDescription>
      )}
      {pageInd == 2 && (
        <PageNumberDescription>Critical Assumptions</PageNumberDescription>
      )}
    </div>
  )

  return (
    <div>
      <Container>
        {pageInd == 0 && (
          <div className="w-full md:flex-row md:items-center justify-center">
            {header}
            <ContentContainer>
              <BTLCalcFieldWithSlider
                text={'What is the purchase price of the property?'}
                value={priceValue}
                step={1_000}
                maxValue={10_000_000}
                maxText={'10 Mio EUR'}
                handler={setPriceValue}
              />

              <BTLCalcFieldWithSlider
                text={'What is the expected rent?'}
                value={rentValue}
                step={1}
                maxValue={100_000}
                maxText={format(100_000) + ' EUR'}
                handler={setRentValue}
              />
            </ContentContainer>

            <SimpleMuiButton
              className="h-[48px] md:h-[72px]"
              onClick={() => movePage(1)}
              variant="contained"
              color="primary"
              endIcon={<ArrowRight />}
            >
              Next Step
            </SimpleMuiButton>
          </div>
        )}

        {pageInd == 1 && (
          <div className="w-full md:flex-row md:items-center justify-center">
            {header}
            <ContentContainer>
              <BTLCalcFieldWithSlider
                text={'How much do you want to put down?'}
                value={putDownValue}
                step={1_000}
                maxValue={10_000_000}
                maxText={'10 Mio EUR'}
                handler={setPutDownValue}
              />

              <BTLCalcField
                decimals={2}
                step={0.01}
                maxValue={5}
                text={'What is the interest rate of the loan?'}
                value={interestRate}
                handler={setInterestRate}
              />

              <BTLCalcField
                decimals={1}
                step={0.1}
                maxValue={5}
                text={'What is the repayment rate of the loan?'}
                value={repaymentRate}
                handler={setRepaymentRate}
              />

              <HorizontalLine />

              <BTLCalcField
                decimals={1}
                step={0.5}
                maxValue={10}
                text={'What is the property transfer tax?'}
                value={propertyTax}
                handler={setPropertyTax}
              />

              <BTLCalcField
                decimals={1}
                step={0.1}
                maxValue={5}
                text={'What is the Notary & land registry fees?'}
                value={notaryFees}
                handler={setNotaryFees}
              />

              <BTLCalcField
                decimals={2}
                step={0.01}
                maxValue={10}
                text={'How high are the real estate agent fees?'}
                value={agentFees}
                handler={setAgentFees}
              />

              <br />
            </ContentContainer>

            <div>
              <SimpleIconButton
                aria-label="Go to previous step"
                className="h-[48px] w-[48px] md:h-[72px] md:w-[72px]"
                onClick={() => movePage(-1)}
                color="secondary"
              >
                <ArrowLeft />
              </SimpleIconButton>
              <SimpleMuiButton
                className="h-[48px] md:h-[72px] ml-px-8! md:ml-px-16!"
                onClick={() => movePage(1)}
                variant="contained"
                color="primary"
                endIcon={<ArrowRight />}
              >
                Next Step
              </SimpleMuiButton>
            </div>
          </div>
        )}

        {pageInd == 2 && (
          <div className="w-full md:flex-row md:items-center justify-center">
            {header}
            <ContentContainer>
              <BTLCalcField
                step={0.1}
                decimals={1}
                maxValue={10}
                text={'How do you expect the property to appreciate annually?'}
                value={appreciateValue}
                handler={setAppreciateValue}
              />

              <BTLCalcField
                decimals={1}
                step={0.1}
                maxValue={10}
                text={'What is the expected annual rental increase?'}
                value={rentalIncrease}
                handler={setRentalIncrease}
              />

              <HorizontalLine />

              <BTLCalcFieldWithSlider
                text={'What is the NET monthly Hausgeld payment?'}
                value={maintenanceValue}
                step={1}
                maxValue={2_000}
                maxText={format(2_000) + ' EUR'}
                handler={setMaintenanceValue}
              />
            </ContentContainer>

            <SimpleIconButton
              aria-label="Go to previous step"
              className="h-[48px] w-[48px] md:h-[72px] md:w-[72px]"
              onClick={() => movePage(-1)}
              color="secondary"
            >
              <ArrowLeft />
            </SimpleIconButton>
            <SimpleMuiButton
              className="h-[48px] md:h-[72px] ml-px-8! md:ml-px-16!"
              onClick={() => movePage(1)}
              variant="contained"
              color="primary"
              endIcon={<ArrowRight />}
            >
              Result
            </SimpleMuiButton>
          </div>
        )}
      </Container>
    </div>
  )
}
